import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  TextField,
  Button,
  Box,
  Avatar,
  Grid,
  Divider,
  IconButton,
  Alert,
  CircularProgress,
  InputAdornment,
  Paper,
} from '@mui/material';
import {
  Save as SaveIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Email as EmailIcon,
  Work as WorkIcon,
  LockOutlined as LockIcon
} from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { getImageUrl } from '../../utils/formatters';

const MeuPerfil = ({ open, onClose }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [userData, setUserData] = useState(null);
  const [photoError, setPhotoError] = useState(false);
  
  // Estados para a alteração de senha
  const [passwordData, setPasswordData] = useState({
    senhaAtual: '',
    novaSenha: '',
    confirmacaoSenha: ''
  });
  const [showPassword, setShowPassword] = useState({
    senhaAtual: false,
    novaSenha: false,
    confirmacaoSenha: false
  });
  const [passwordEditMode, setPasswordEditMode] = useState(false);
  const [passwordError, setPasswordError] = useState(null);
  const [passwordSuccess, setPasswordSuccess] = useState(null);

  useEffect(() => {
    if (open && user) {
      loadUserData();
    }
  }, [open, user]);

  const loadUserData = async () => {
    if (!user || !user.id) return;
    
    setLoading(true);
    setPasswordError(null);
    
    try {
      const response = await api.get(`/usuarios/${user.id}`);
      if (response.data && response.data.success) {
        setUserData(response.data.data);
        setPhotoError(false);
      } else {
        setPasswordError('Não foi possível carregar os dados do usuário');
      }
    } catch (err) {
      console.error('Erro ao carregar dados do usuário:', err);
      setPasswordError('Erro ao carregar dados do usuário');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData({ ...passwordData, [name]: value });
    setPasswordError(null);
  };

  const togglePasswordVisibility = (field) => {
    setShowPassword({
      ...showPassword,
      [field]: !showPassword[field]
    });
  };

  const handleChangePassword = async () => {
    // Validações
    if (!passwordData.senhaAtual) {
      setPasswordError('A senha atual é obrigatória');
      return;
    }
    
    if (!passwordData.novaSenha) {
      setPasswordError('A nova senha é obrigatória');
      return;
    }
    
    if (passwordData.novaSenha !== passwordData.confirmacaoSenha) {
      setPasswordError('A confirmação de senha não corresponde à nova senha');
      return;
    }
    
    setSaving(true);
    setPasswordError(null);
    setPasswordSuccess(null);
    
    try {
      // Enviar requisição para alteração de senha
      const response = await api.put(`/usuarios/${user.id}/senha`, {
        senha_atual: passwordData.senhaAtual,
        nova_senha: passwordData.novaSenha
      });
      
      if (response.data && response.data.success) {
        setPasswordSuccess('Senha alterada com sucesso!');
        // Limpar campos de senha
        setPasswordData({
          senhaAtual: '',
          novaSenha: '',
          confirmacaoSenha: ''
        });
        setPasswordEditMode(false);
      } else {
        setPasswordError('Não foi possível alterar a senha');
      }
    } catch (err) {
      console.error('Erro ao alterar senha:', err);
      setPasswordError('Erro ao alterar senha: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleImageError = () => {
    setPhotoError(true);
  };

  // Função para obter as iniciais do nome para o Avatar
  const getInitials = (name) => {
    if (!name) return '';
    return name
      .split(' ')
      .map(word => word[0])
      .slice(0, 2)
      .join('')
      .toUpperCase();
  };

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogContent>
          <Box display="flex" justifyContent="center" alignItems="center" p={4}>
            <CircularProgress />
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          boxShadow: 10
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="div" fontWeight="bold">
            Meu Perfil
          </Typography>
          <IconButton 
            edge="end" 
            color="inherit" 
            onClick={onClose} 
            aria-label="fechar"
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {userData && (
          <Box>
            {/* Seção de informações do Perfil (apenas visualização) */}
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3, 
                mb: 3, 
                borderRadius: 2,
                bgcolor: 'background.paper' 
              }}
            >
              <Box 
                display="flex" 
                flexDirection="column" 
                alignItems="center" 
                mb={3}
              >
                <Box mb={2}>
                  <Avatar
                    src={(!photoError && userData.imagem_perfil) ? getImageUrl(userData.imagem_perfil) : ''}
                    onError={handleImageError}
                    sx={{
                      width: 120,
                      height: 120,
                      fontSize: 48,
                      bgcolor: 'primary.main',
                      boxShadow: 3
                    }}
                  >
                    {getInitials(userData.nome)}
                  </Avatar>
                </Box>
                
                <Typography 
                  variant="h5" 
                  gutterBottom 
                  fontWeight="bold"
                  textAlign="center"
                >
                  {userData.nome}
                </Typography>
                
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                  mb={1}
                >
                  <EmailIcon color="action" fontSize="small" />
                  <Typography 
                    variant="body1" 
                    color="text.secondary"
                  >
                    {userData.email}
                  </Typography>
                </Box>
                
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  gap={1}
                >
                  <WorkIcon color="primary" fontSize="small" />
                  <Typography 
                    variant="body2" 
                    color="primary"
                    fontWeight="medium"
                  >
                    {userData.cargo}
                  </Typography>
                </Box>
              </Box>
            </Paper>
            
            {/* Seção de Alteração de Senha */}
            <Paper 
              elevation={2} 
              sx={{ 
                p: 3, 
                borderRadius: 2,
                bgcolor: 'background.paper' 
              }}
            >
              <Box 
                display="flex" 
                justifyContent="space-between" 
                alignItems="center" 
                mb={2}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <LockIcon color="primary" />
                  <Typography variant="h6" fontWeight="bold">
                    Alterar Senha
                  </Typography>
                </Box>
                
                {!passwordEditMode && (
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => setPasswordEditMode(true)}
                  >
                    Alterar
                  </Button>
                )}
              </Box>
              
              {passwordError && (
                <Alert 
                  severity="error" 
                  sx={{ mb: 2 }} 
                  onClose={() => setPasswordError(null)}
                >
                  {passwordError}
                </Alert>
              )}
              
              {passwordSuccess && (
                <Alert 
                  severity="success" 
                  sx={{ mb: 2 }} 
                  onClose={() => setPasswordSuccess(null)}
                >
                  {passwordSuccess}
                </Alert>
              )}
              
              {passwordEditMode && (
                <Box>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Senha Atual"
                        name="senhaAtual"
                        type={showPassword.senhaAtual ? 'text' : 'password'}
                        value={passwordData.senhaAtual}
                        onChange={handlePasswordChange}
                        variant="outlined"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => togglePasswordVisibility('senhaAtual')}
                                edge="end"
                              >
                                {showPassword.senhaAtual ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Nova Senha"
                        name="novaSenha"
                        type={showPassword.novaSenha ? 'text' : 'password'}
                        value={passwordData.novaSenha}
                        onChange={handlePasswordChange}
                        variant="outlined"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => togglePasswordVisibility('novaSenha')}
                                edge="end"
                              >
                                {showPassword.novaSenha ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Confirmar Nova Senha"
                        name="confirmacaoSenha"
                        type={showPassword.confirmacaoSenha ? 'text' : 'password'}
                        value={passwordData.confirmacaoSenha}
                        onChange={handlePasswordChange}
                        variant="outlined"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={() => togglePasswordVisibility('confirmacaoSenha')}
                                edge="end"
                              >
                                {showPassword.confirmacaoSenha ? <VisibilityOffIcon /> : <VisibilityIcon />}
                              </IconButton>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Grid>
                  </Grid>
                  
                  <Box 
                    display="flex" 
                    justifyContent="flex-end" 
                    gap={1} 
                    mt={2}
                  >
                    <Button
                      variant="outlined"
                      onClick={() => {
                        setPasswordEditMode(false);
                        setPasswordData({
                          senhaAtual: '',
                          novaSenha: '',
                          confirmacaoSenha: ''
                        });
                        setPasswordError(null);
                      }}
                      disabled={saving}
                    >
                      Cancelar
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
                      onClick={handleChangePassword}
                      disabled={saving}
                    >
                      Salvar
                    </Button>
                  </Box>
                </Box>
              )}
            </Paper>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MeuPerfil; 